import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import api from '@/lib/api'
import {
  Hourglass, Store, UserPlus, Plus, Search, Loader2, AlertTriangle, MessageSquare, Send, Trash2,
  CheckCircle2, X, Truck, PackageCheck, PackageX, ExternalLink, RefreshCw, StickyNote, ChevronDown,
  Globe, CalendarClock, Tag, Hash, ClipboardList, MessagesSquare, Star, Pencil, Megaphone, Clock, Building2,
} from 'lucide-react'

const PS_ORDER_URL = 'https://www.jvs-informatica.com/admin058mixixqnew/index.php/sell/orders/{id}/view'

interface Comment {
  id: number
  item_key: string
  text: string
  author: string
  author_name: string | null
  created_at: string
}

interface PendingPart {
  key: string
  origin: 'prestashop' | 'manual'
  origin_label: string
  status: 'rosa' | 'contrarreembolso' | 'manual'
  id: number | null
  reference: string
  description: string
  supplier_code: string | null
  supplier_name: string | null
  supplier_reference: string | null
  stock: number
  supplier_orders: { order: number; units: number }[]
  registered: boolean
  supply: SupplyAlloc[]
  supply_status: 'en_stock' | 'pedido_proveedor' | 'pedido_web' | 'sin_pedir'
  uncovered_units: number
  eta: SupplyAlloc | null
  units: number
  id_order: number | null
  order_reference: string | null
  customer: string | null
  product_name: string | null
  date: string
  days_waiting: number
  note: string | null
  created_by: string | null
  comments: Comment[]
}

interface SupplyAlloc {
  type: 'stock' | 'supplier_order' | 'web_order'
  units: number
  supplier_order?: number
  web_order?: string
  order_date?: string
  supplier_code?: string
  supplier_name?: string | null
  supplier_doc?: string | null
  arrival_from?: string
  arrival_to?: string
  delivery_from?: string
  delivery_to?: string
  late?: boolean
}

interface SearchResult {
  reference: string | null
  supplier_reference: string | null
  supplier_code: string | null
  name: string
  registered: boolean
  stock: number | null
}

interface PendingPartsResponse {
  catalog_suppliers: { code: string; name: string | null }[]
  items: PendingPart[]
  totals: { all: number; rosa: number; contrarreembolso: number; manual: number }
  suppliers: { code: string; name: string | null }[]
  generated_at: string
  current_user: string
  is_admin: boolean
}

type OriginFilter = 'all' | 'rosa' | 'contrarreembolso' | 'manual'

// Estilo por origen: PrestaShop (rosa pagado / ámbar contrarreembolso) frente a manual (índigo)
const STYLE = {
  rosa: {
    stripe: 'bg-pink-500',
    badge: 'bg-pink-100 text-pink-700 ring-pink-200 dark:bg-pink-900/40 dark:text-pink-300 dark:ring-pink-800',
    card: 'border-pink-200/70 dark:border-pink-900/50',
    label: 'Pagado · pendiente de stock',
  },
  contrarreembolso: {
    stripe: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800',
    card: 'border-amber-200/70 dark:border-amber-900/50',
    label: 'Contrarreembolso · en espera de pago',
  },
  manual: {
    stripe: 'bg-gradient-to-b from-indigo-500 to-violet-500',
    badge: 'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:ring-indigo-800',
    card: 'border-indigo-200/70 dark:border-indigo-900/50 bg-gradient-to-r from-indigo-50/60 to-transparent dark:from-indigo-950/30',
    label: 'Añadida a mano',
  },
} as const

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?'

const fmtDate = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)}${s.length > 10 ? ' ' + s.slice(11, 16) : ''}`

function Avatar({ name, className = '' }: { name: string; className?: string }) {
  return (
    <span className={`inline-flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-semibold ${className}`}>
      {initials(name)}
    </span>
  )
}

function AgeBadge({ days }: { days: number }) {
  const cls = days >= 15
    ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300'
    : days >= 7
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`} title="Días esperando">
      <Hourglass className="w-3 h-3" />
      {days === 0 ? 'hoy' : `${days} d`}
    </span>
  )
}

const shortName = (n?: string | null) => (n || '').split(/[\s/]+/)[0] || 'proveedor'
const range = (a?: string, b?: string) => {
  if (!a || !b) return ''
  return a === b ? fmtDate(a) : `${fmtDate(a)} – ${fmtDate(b)}`
}

function SupplyStatus({ part }: { part: PendingPart }) {
  const cfg = {
    en_stock: { icon: PackageCheck, text: 'En stock', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
    pedido_proveedor: { icon: Truck, text: 'Pedida al proveedor', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    pedido_web: { icon: Globe, text: 'Pedida en web (sin factura)', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
    sin_pedir: { icon: PackageX, text: 'Sin pedir al proveedor', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  }[part.supply_status]
  const late = part.eta?.late && part.supply_status !== 'en_stock'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${late ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : cfg.cls}`}>
      <cfg.icon className="w-3 h-3" /> {late ? 'Con retraso' : cfg.text}
    </span>
  )
}

// Situación explicada para atender al cliente («¿cómo va mi pedido?»)
function SupplyPanel({ part }: { part: PendingPart }) {
  const lines = part.supply.map((a, i) => {
    if (a.type === 'stock') {
      return (
        <div key={i} className="flex items-start gap-2 text-emerald-800 dark:text-emerald-300">
          <PackageCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>{a.units} ud en stock</b> en el almacén: lista para preparar y enviar.</span>
        </div>
      )
    }
    const isWeb = a.type === 'web_order'
    return (
      <div key={i} className={`flex items-start gap-2 ${a.late ? 'text-amber-800 dark:text-amber-300' : isWeb ? 'text-sky-800 dark:text-sky-300' : 'text-blue-800 dark:text-blue-300'}`}>
        {isWeb ? <Globe className="w-4 h-4 mt-0.5 shrink-0" /> : <Truck className="w-4 h-4 mt-0.5 shrink-0" />}
        <span>
          <b>{a.units} ud {isWeb ? `pedida${a.units === 1 ? '' : 's'} en la web de ${shortName(a.supplier_name)}` : `en el pedido a ${shortName(a.supplier_name)} nº ${a.supplier_order}`}</b>
          {' '}del {fmtDate(a.order_date || '')}
          {isWeb && <span className="opacity-80"> (pedido web {a.web_order}, pendiente de su factura)</span>}
          {!isWeb && a.supplier_doc && <span className="opacity-70"> · {a.supplier_doc}</span>}
          {a.late ? (
            <span className="block mt-0.5 font-medium">
              <AlertTriangle className="inline w-3.5 h-3.5 -mt-0.5" /> Debía llegar como tarde el {fmtDate(a.arrival_to || '')} y aún no ha entrado: conviene consultar al proveedor.
            </span>
          ) : (
            <span className="block mt-0.5">
              <CalendarClock className="inline w-3.5 h-3.5 -mt-0.5" /> Llegada al almacén aprox. <b>{range(a.arrival_from, a.arrival_to)}</b>
              {' '}· entrega al cliente aprox. <b>{range(a.delivery_from, a.delivery_to)}</b>
            </span>
          )}
        </span>
      </div>
    )
  })
  return (
    <div className="mt-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40 px-3 py-2 space-y-1.5 text-sm">
      {lines}
      {part.uncovered_units > 0 && (
        <div className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
          <PackageX className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>{part.uncovered_units} ud sin pedir</b> todavía al proveedor: saldrá{part.uncovered_units === 1 ? '' : 'n'} en el próximo pedido{part.supplier_name ? ` a ${shortName(part.supplier_name)}` : ''}.</span>
        </div>
      )}
    </div>
  )
}

function CommentsThread({ part, currentUser, isAdmin }: { part: PendingPart; currentUser: string; isAdmin: boolean }) {
  const qc = useQueryClient()
  const [textValue, setTextValue] = useState('')
  const add = useMutation({
    mutationFn: async () => api.post('/reports/pending-parts/comments', { key: part.key, text: textValue }),
    onSuccess: () => { setTextValue(''); qc.invalidateQueries({ queryKey: ['pending-parts'] }) },
  })
  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/reports/pending-parts/comments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-parts'] }),
  })

  return (
    <div className="mt-3 pt-3 border-t border-slate-200/80 dark:border-slate-700 space-y-3">
      {part.comments.length === 0 && (
        <p className="text-xs text-slate-400 italic">Sin comentarios todavía.</p>
      )}
      {part.comments.map((c) => (
        <div key={c.id} className="flex gap-2.5 group">
          <Avatar name={c.author_name || c.author} className="w-7 h-7 text-[11px] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{c.author_name || c.author}</span>
              <span className="text-[11px] text-slate-400">{fmtDate(c.created_at)}</span>
              {(c.author === currentUser || isAdmin) && (
                <button
                  onClick={() => { if (confirm('¿Borrar este comentario?')) del.mutate(c.id) }}
                  className="ml-auto opacity-0 group-hover:opacity-100 text-slate-400 hover:text-danger-600 transition-opacity"
                  title="Borrar comentario"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-words">{c.text}</p>
          </div>
        </div>
      ))}
      <form
        onSubmit={(e) => { e.preventDefault(); if (textValue.trim()) add.mutate() }}
        className="flex gap-2"
      >
        <input
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          placeholder="Añadir un comentario…"
          className="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="submit"
          disabled={!textValue.trim() || add.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar
        </button>
      </form>
    </div>
  )
}

function PartCard({ part, currentUser, isAdmin }: { part: PendingPart; currentUser: string; isAdmin: boolean }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [reason, setReason] = useState('')
  const st = STYLE[part.status]
  const close = useMutation({
    mutationFn: async () => api.post(`/reports/pending-parts/${part.id}/close`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-parts'] }),
  })

  return (
    <div className={`relative overflow-hidden rounded-xl border bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow ${st.card}`}>
      <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${st.stripe}`} />
      <div className="pl-5 pr-4 py-3.5">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          {/* Origen */}
          <div className="w-44 shrink-0">
            {part.origin === 'prestashop' ? (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ring-1 ${st.badge}`}>
                <Store className="w-3.5 h-3.5" /> PrestaShop
              </span>
            ) : (
              <span className={`inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-lg text-xs font-semibold ring-1 ${st.badge}`}>
                <Avatar name={part.origin_label} className="w-5 h-5 text-[9px]" />
                {part.origin_label}
              </span>
            )}
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{st.label}</p>
          </div>

          {/* Pieza */}
          <div className="flex-1 min-w-[16rem]">
            <div className="flex flex-wrap items-baseline gap-x-2">
              {part.registered ? (
                <>
                  <span className="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400">{part.reference}</span>
                  {part.supplier_reference && (
                    <span className="font-mono text-xs text-slate-500 dark:text-slate-400">({part.supplier_reference})</span>
                  )}
                </>
              ) : (
                <>
                  <span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">{part.supplier_reference}</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Producto sin alta en Factusol: se pide por la referencia del proveedor">
                    sin alta
                  </span>
                </>
              )}
              <span className="text-xs text-slate-400">· {part.supplier_name || 'sin proveedor'}</span>
            </div>
            <p className="text-sm text-slate-800 dark:text-slate-200 line-clamp-1" title={part.description || part.product_name || ''}>
              {part.description || part.product_name}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500 dark:text-slate-400">
              {part.id_order ? (
                <a
                  href={PS_ORDER_URL.replace('{id}', String(part.id_order))}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300 hover:text-indigo-600"
                >
                  Pedido {part.id_order} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span className="italic">sin pedido asociado</span>
              )}
              {part.customer && <span>{part.customer}</span>}
              <span>{part.origin === 'manual' ? 'añadida' : 'pedido'} el {fmtDate(part.date)}</span>
            </div>
          </div>

          {/* Estado */}
          <div className="flex flex-col items-end gap-1.5 ml-auto">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-900 dark:text-white">{part.units}</span>
              <span className="text-xs text-slate-500">ud{part.units === 1 ? '' : 's'}</span>
              <AgeBadge days={part.days_waiting} />
            </div>
            <SupplyStatus part={part} />
          </div>
        </div>

        <SupplyPanel part={part} />

        {part.note && (
          <div className="mt-2.5 flex gap-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 px-3 py-2 text-sm text-indigo-900 dark:text-indigo-200">
            <StickyNote className="w-4 h-4 mt-0.5 shrink-0 text-indigo-400" />
            <span className="whitespace-pre-wrap">{part.note}</span>
          </div>
        )}

        <div className="flex items-center gap-2 mt-2.5">
          <button
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
              part.comments.length
                ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {part.comments.length ? `${part.comments.length} comentario${part.comments.length === 1 ? '' : 's'}` : 'Comentar'}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {part.comments.length > 0 && !open && (
            <span className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-md">
              <b className="font-medium">{part.comments[part.comments.length - 1].author_name || part.comments[part.comments.length - 1].author}:</b>{' '}
              {part.comments[part.comments.length - 1].text}
            </span>
          )}
          {part.origin === 'manual' && !closing && (
            <button
              onClick={() => setClosing(true)}
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
              title="La pieza ya se ha enviado o ya no hace falta: deja de contar en los pedidos a proveedor"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Cerrar
            </button>
          )}
        </div>

        {closing && (
          <form
            onSubmit={(e) => { e.preventDefault(); close.mutate() }}
            className="mt-2.5 flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-2.5"
          >
            <input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo (opcional): enviada, anulada por el cliente…"
              className="flex-1 min-w-[14rem] px-3 py-1.5 text-sm border border-emerald-300 dark:border-emerald-800 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
            <button type="submit" disabled={close.isPending} className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              {close.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cerrar pieza'}
            </button>
            <button type="button" onClick={() => setClosing(false)} className="px-2 py-1.5 text-sm text-slate-500 hover:text-slate-800">
              Cancelar
            </button>
          </form>
        )}

        {open && <CommentsThread part={part} currentUser={currentUser} isAdmin={isAdmin} />}
      </div>
    </div>
  )
}

function AddPartModal({ onClose, catalogSuppliers }: { onClose: () => void; catalogSuppliers: { code: string; name: string | null }[] }) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<'jvs' | 'supplier'>('jvs')
  const [supplier, setSupplier] = useState(catalogSuppliers.find((c) => c.code === '23')?.code || catalogSuppliers[0]?.code || '')
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [selected, setSelected] = useState<SearchResult | null>(null)
  const [freeName, setFreeName] = useState('')
  const [units, setUnits] = useState('1')
  const [order, setOrder] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350)
    return () => clearTimeout(t)
  }, [q])

  const search = useQuery({
    queryKey: ['pending-parts-search', mode, supplier, debounced],
    enabled: debounced.length >= 2 && !selected,
    queryFn: async () => {
      const res = await api.get<{ results: SearchResult[] }>('/reports/pending-parts/search', {
        params: { q: debounced, mode, ...(mode === 'supplier' ? { supplier } : {}) },
      })
      return res.data.results
    },
  })

  const switchMode = (m: 'jvs' | 'supplier') => { setMode(m); setSelected(null); setQ(''); setFreeName('') }
  const supplierName = catalogSuppliers.find((c) => c.code === supplier)?.name || ''
  // en modo proveedor se puede usar una referencia que no esté en el catálogo («tal cual», sin alta)
  const freeRef = mode === 'supplier' && !selected && q.trim().length >= 2 ? q.trim() : ''

  const create = useMutation({
    mutationFn: async () => {
      const base = { units: Number(units), id_order: order ? Number(order) : null, note: note || null }
      if (selected?.registered && selected.reference) {
        return api.post('/reports/pending-parts', { ...base, reference: selected.reference })
      }
      return api.post('/reports/pending-parts', {
        ...base,
        supplier_code: supplier,
        supplier_reference: selected ? selected.supplier_reference : freeRef,
        product_name: selected ? selected.name : freeName,
      })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pending-parts'] }); onClose() },
  })
  const err = (create.error as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail
  const canSubmit = !!selected || (!!freeRef && freeName.trim().length > 2)

  const inputCls = 'w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            <h2 className="font-semibold">Añadir pieza pendiente</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) create.mutate() }} className="p-5 space-y-4 overflow-y-auto">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Para piezas que un cliente espera y PrestaShop no ve (por ejemplo, un envío parcial). Contará en el
            pedido al proveedor hasta que la cierres.
          </p>

          {/* Tipo de referencia */}
          <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-900">
            {([['jvs', 'Referencia JVS', Hash], ['supplier', 'Referencia de proveedor', Tag]] as const).map(([m, label, Icon]) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === m ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          {mode === 'supplier' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Proveedor *</label>
              <select value={supplier} onChange={(e) => { setSupplier(e.target.value); setSelected(null) }} className={inputCls}>
                {catalogSuppliers.map((c) => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
              </select>
            </div>
          )}

          {/* Buscador / seleccionado */}
          {selected ? (
            <div className="flex items-start gap-3 rounded-xl border-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50/70 dark:bg-indigo-950/40 p-3">
              <PackageCheck className="w-5 h-5 mt-0.5 text-indigo-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  {selected.registered && selected.reference ? (
                    <span className="font-mono font-semibold text-indigo-700 dark:text-indigo-300">{selected.reference}</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-800">sin alta</span>
                  )}
                  {selected.supplier_reference && <span className="font-mono text-sm text-slate-500">({selected.supplier_reference})</span>}
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-200">{selected.name}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700" title="Cambiar producto">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Buscar producto por {mode === 'jvs' ? 'referencia JVS o nombre' : `referencia de ${shortName(supplierName)} o nombre`} *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={mode === 'jvs' ? 'PANT-INT-043, Xiaomi Air…' : '1-SCR0065, 81337, E480…'}
                  className={`${inputCls} pl-10`}
                />
                {search.isFetching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-indigo-500" />}
              </div>
              {debounced.length >= 2 && (search.data || []).length > 0 && (
                <ul className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                  {(search.data || []).map((r, i) => (
                    <li key={`${r.reference}-${r.supplier_reference}-${i}`}>
                      <button
                        type="button"
                        onClick={() => setSelected(r)}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                      >
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          {r.reference ? (
                            <span className="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400">{r.reference}</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-800">sin alta</span>
                          )}
                          {r.supplier_reference && <span className="font-mono text-xs text-slate-500">{r.reference ? `(${r.supplier_reference})` : r.supplier_reference}</span>}
                          {r.stock != null && mode === 'supplier' && (
                            <span className="ml-auto text-[11px] text-slate-400">stock prov. {r.stock}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-1">{r.name}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {debounced.length >= 2 && !search.isFetching && (search.data || []).length === 0 && (
                <p className="mt-2 text-sm text-slate-500">No hay resultados{mode === 'jvs' ? '. Si no está dado de alta, búscalo por referencia de proveedor.' : ' en el catálogo.'}</p>
              )}
              {freeRef && (
                <div className="mt-3 rounded-lg border border-dashed border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2">
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    ¿No aparece? Puedes añadir <b className="font-mono">{freeRef}</b> tal cual, como producto sin alta de {shortName(supplierName)}. Escribe su nombre:
                  </p>
                  <input value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Nombre del producto" className={inputCls} />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Unidades *</label>
              <input type="number" min="1" step="1" required value={units} onChange={(e) => setUnits(e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nº de pedido PrestaShop</label>
              <input type="number" value={order} onChange={(e) => setOrder(e.target.value)} placeholder="135926 (opcional)" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Observaciones</label>
            <textarea
              rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Ej.: se enviaron las baterías el 28/09; falta la pantalla"
              className={inputCls}
            />
          </div>
          {err && (
            <div className="flex items-center gap-2 rounded-lg bg-danger-50 dark:bg-danger-900/30 px-3 py-2 text-sm text-danger-700 dark:text-danger-300">
              <AlertTriangle className="w-4 h-4" /> {err}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">
              Cancelar
            </button>
            <button type="submit" disabled={!canSubmit || create.isPending} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
              {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Añadir
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================================ Comentarios generales

interface GeneralComment {
  id: number
  text: string
  supplier_code: string | null
  supplier_name: string | null
  important: boolean
  author: string
  author_name: string | null
  created_at: string
  updated_at: string | null
  updated_by_name: string | null
}

interface GeneralCommentsResponse {
  comments: GeneralComment[]
  suppliers: { code: string; name: string }[]
  current_user: string
  is_admin: boolean
}

type Supplier = { code: string; name: string }

const fmtFull = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)} · ${s.slice(11, 16)}`

function fmtAgo(s: string) {
  const mins = Math.floor((Date.now() - new Date(s.replace(' ', 'T')).getTime()) / 60000)
  if (mins < 1) return 'ahora mismo'
  if (mins < 60) return `hace ${mins} min`
  const h = Math.floor(mins / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  if (d < 30) return `hace ${d} día${d === 1 ? '' : 's'}`
  const mo = Math.floor(d / 30)
  return `hace ${mo} mes${mo === 1 ? '' : 'es'}`
}

// Color estable por proveedor para reconocerlo de un vistazo
const SUPPLIER_TONES = [
  'bg-sky-100 text-sky-800 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-300 dark:ring-sky-800',
  'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-800',
  'bg-violet-100 text-violet-800 ring-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:ring-violet-800',
  'bg-teal-100 text-teal-800 ring-teal-200 dark:bg-teal-900/40 dark:text-teal-300 dark:ring-teal-800',
  'bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200 dark:bg-fuchsia-900/40 dark:text-fuchsia-300 dark:ring-fuchsia-800',
  'bg-cyan-100 text-cyan-800 ring-cyan-200 dark:bg-cyan-900/40 dark:text-cyan-300 dark:ring-cyan-800',
  'bg-lime-100 text-lime-800 ring-lime-200 dark:bg-lime-900/40 dark:text-lime-300 dark:ring-lime-800',
]

function SupplierChip({ code, name }: { code: string | null; name: string | null }) {
  if (!code) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600">
        <MessagesSquare className="w-3 h-3" /> General
      </span>
    )
  }
  const tone = SUPPLIER_TONES[Number(code) % SUPPLIER_TONES.length] || SUPPLIER_TONES[0]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ring-1 ${tone}`}>
      <Building2 className="w-3 h-3" /> {name || code}
    </span>
  )
}

function ImportantSwitch({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full text-sm font-medium ring-1 transition-all ${
        value
          ? 'bg-amber-100 text-amber-900 ring-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-700'
          : 'bg-white text-slate-600 ring-slate-300 hover:ring-slate-400 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-600'
      }`}
      title="Los importantes se muestran destacados arriba del todo de la sección"
    >
      <span className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors ${value ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-400 dark:bg-slate-700'}`}>
        <Star className={`w-3.5 h-3.5 ${value ? 'fill-current' : ''}`} />
      </span>
      Importante
    </button>
  )
}

const apiError = (e: unknown) =>
  (e as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail || (e ? 'No se pudo guardar' : '')

// Formulario común para crear y editar un comentario general
function CommentEditor({
  suppliers, initial, submitLabel, pending, error, onSubmit, onCancel, autoFocus,
}: {
  suppliers: Supplier[]
  initial?: { text: string; supplier_code: string | null; important: boolean }
  submitLabel: string
  pending: boolean
  error?: string
  onSubmit: (v: { text: string; supplier_code: string | null; important: boolean }) => void
  onCancel?: () => void
  autoFocus?: boolean
}) {
  const [text, setText] = useState(initial?.text || '')
  const [supplier, setSupplier] = useState(initial?.supplier_code || '')
  const [important, setImportant] = useState(initial?.important || false)
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { if (autoFocus) ref.current?.focus() }, [autoFocus])
  const submit = () => { if (text.trim()) onSubmit({ text: text.trim(), supplier_code: supplier || null, important }) }

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit() }} className="space-y-3">
      <textarea
        ref={ref}
        rows={initial ? 3 : 4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit() } }}
        placeholder="Ej.: ITSYS avisa de que esta semana no sale contenedor; las pantallas de 40 pines llegan el día 20."
        className="w-full px-3.5 py-3 text-sm leading-relaxed border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
      />
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative flex-1 min-w-[12rem]">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <select
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          >
            <option value="">Sin proveedor (general)</option>
            {suppliers.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </div>
        <ImportantSwitch value={important} onChange={setImportant} />
      </div>
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-danger-50 dark:bg-danger-900/30 px-3 py-2 text-sm text-danger-700 dark:text-danger-300">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        {!initial && <span className="mr-auto text-[11px] text-slate-400 hidden sm:inline">Ctrl + Enter para publicar</span>}
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={!text.trim() || pending}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium shadow-sm hover:shadow-md disabled:opacity-50 disabled:shadow-none transition-shadow"
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : initial ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
          {submitLabel}
        </button>
      </div>
    </form>
  )
}

function useGeneralMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: ['pending-parts-general'] })
  const create = useMutation({
    mutationFn: async (v: { text: string; supplier_code: string | null; important: boolean }) =>
      api.post('/reports/pending-parts/general-comments', v),
    onSuccess: refresh,
  })
  const update = useMutation({
    mutationFn: async ({ id, ...v }: { id: number; text?: string; supplier_code?: string | null; important?: boolean }) =>
      api.put(`/reports/pending-parts/general-comments/${id}`, v),
    onSuccess: refresh,
  })
  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/reports/pending-parts/general-comments/${id}`),
    onSuccess: refresh,
  })
  return { create, update, remove }
}

function useGeneralComments() {
  return useQuery({
    queryKey: ['pending-parts-general'],
    queryFn: async () => (await api.get<GeneralCommentsResponse>('/reports/pending-parts/general-comments')).data,
    retry: (n, e) => (e as { response?: { status?: number } })?.response?.status !== 404 && n < 2,
  })
}

const isNotDeployed = (e: unknown) => (e as { response?: { status?: number } } | null)?.response?.status === 404

function GeneralCommentCard({
  c, suppliers, canEdit, variant = 'normal',
}: { c: GeneralComment; suppliers: Supplier[]; canEdit: boolean; variant?: 'normal' | 'important' }) {
  const { update, remove } = useGeneralMutations()
  const [editing, setEditing] = useState(false)
  const author = c.author_name || c.author
  const important = variant === 'important'

  return (
    <article
      className={`group relative overflow-hidden rounded-xl transition-shadow ${
        important
          ? 'bg-white/90 dark:bg-slate-900/70 border border-amber-300/80 dark:border-amber-700/60 shadow-sm hover:shadow-md'
          : c.important
            ? 'bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/60'
            : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md'
      }`}
    >
      {important && <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-amber-400 via-orange-500 to-rose-500" />}
      <div className={`${important ? 'pl-5' : 'pl-4'} pr-4 py-3.5`}>
        <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <Avatar name={author} className="w-8 h-8 text-xs shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-tight">{author}</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 inline-flex items-center gap-1" title={`Creado el ${fmtFull(c.created_at)}`}>
              <Clock className="w-3 h-3" /> {fmtFull(c.created_at)}
              <span className="text-slate-400">· {fmtAgo(c.created_at)}</span>
              {c.updated_at && (
                <span className="italic text-slate-400" title={`Editado el ${fmtFull(c.updated_at)} por ${c.updated_by_name || ''}`}>· editado</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <SupplierChip code={c.supplier_code} name={c.supplier_name} />
            {c.important && !important && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-500 text-white">
                <Star className="w-3 h-3 fill-current" /> Importante
              </span>
            )}
          </div>
        </header>

        {editing ? (
          <div className="mt-3">
            <CommentEditor
              autoFocus
              suppliers={suppliers}
              initial={{ text: c.text, supplier_code: c.supplier_code, important: c.important }}
              submitLabel="Guardar"
              pending={update.isPending}
              error={update.isError ? apiError(update.error) : undefined}
              onCancel={() => { setEditing(false); update.reset() }}
              onSubmit={(v) => update.mutate({ id: c.id, ...v }, { onSuccess: () => setEditing(false) })}
            />
          </div>
        ) : (
          <p className={`mt-2.5 whitespace-pre-wrap break-words leading-relaxed ${
            important ? 'text-[15px] font-medium text-slate-900 dark:text-white' : 'text-sm text-slate-700 dark:text-slate-200'
          }`}>
            {c.text}
          </p>
        )}

        {canEdit && !editing && (
          <footer className="flex items-center justify-end gap-1 mt-2 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity">
            <button
              onClick={() => update.mutate({ id: c.id, important: !c.important })}
              disabled={update.isPending}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                c.important
                  ? 'text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-amber-600 dark:text-slate-400 dark:hover:bg-slate-700'
              }`}
              title={c.important ? 'Quitar de importantes' : 'Marcar como importante'}
            >
              <Star className={`w-3.5 h-3.5 ${c.important ? 'fill-current' : ''}`} /> {c.important ? 'Quitar destacado' : 'Destacar'}
            </button>
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-indigo-600 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              <Pencil className="w-3.5 h-3.5" /> Editar
            </button>
            <button
              onClick={() => { if (confirm('¿Borrar este comentario? No se puede deshacer.')) remove.mutate(c.id) }}
              disabled={remove.isPending}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:bg-danger-50 hover:text-danger-600 dark:text-slate-400 dark:hover:bg-danger-900/30"
            >
              {remove.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Borrar
            </button>
          </footer>
        )}
      </div>
    </article>
  )
}

// Avisos importantes: arriba del todo de la sección, visibles en las dos pestañas
function ImportantBoard({ data }: { data: GeneralCommentsResponse }) {
  const items = data.comments.filter((c) => c.important)
  if (!items.length) return null
  return (
    <section className="relative overflow-hidden rounded-2xl border-2 border-amber-300 dark:border-amber-700/70 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-rose-950/30 shadow-md">
      <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white">
        <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/20 ring-1 ring-white/40">
          <Megaphone className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <h2 className="font-bold tracking-tight leading-tight">Avisos importantes</h2>
          <p className="text-xs text-white/85">Léelos antes de atender a un cliente o hacer un pedido a proveedor</p>
        </div>
        <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-full bg-white text-orange-600 font-bold text-sm shadow-sm">
          {items.length}
        </span>
      </div>
      <div className={`grid gap-3 p-4 ${items.length > 1 ? 'lg:grid-cols-2' : ''}`}>
        {items.map((c) => (
          <GeneralCommentCard
            key={c.id}
            c={c}
            variant="important"
            suppliers={data.suppliers}
            canEdit={c.author === data.current_user || data.is_admin}
          />
        ))}
      </div>
    </section>
  )
}

function GeneralCommentsTab({ query }: { query: ReturnType<typeof useGeneralComments> }) {
  const { create } = useGeneralMutations()
  const [composerKey, setComposerKey] = useState(0)
  const [supplier, setSupplier] = useState('')
  const [search, setSearch] = useState('')
  const data = query.data

  const all = data?.comments || []
  const usedSuppliers = useMemo(() => {
    const m = new Map<string, string>()
    all.forEach((c) => { if (c.supplier_code) m.set(c.supplier_code, c.supplier_name || c.supplier_code) })
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [all])

  const list = useMemo(() => {
    const q = search.trim().toUpperCase()
    return all.filter((c) => {
      if (c.important) return false  // ya están arriba, en «Avisos importantes»
      if (supplier === '_none' ? c.supplier_code : supplier && c.supplier_code !== supplier) return false
      return !q || [c.text, c.author_name, c.author, c.supplier_name].some((v) => (v || '').toUpperCase().includes(q))
    })
  }, [all, supplier, search])

  if (query.isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        <p className="text-slate-500">Cargando comentarios…</p>
      </div>
    )
  }
  if (query.isError) {
    return isNotDeployed(query.error) ? (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <span className="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40"><Clock className="w-8 h-8 text-indigo-500" /></span>
        <p className="font-medium text-slate-700 dark:text-slate-200">Los comentarios generales se activan con el próximo reinicio del servidor</p>
        <p className="text-sm text-slate-500 max-w-md">La pestaña ya está lista; en cuanto se reinicie el backend podrás crear, editar y destacar comentarios.</p>
      </div>
    ) : (
      <div className="flex flex-col items-center gap-3 py-16">
        <AlertTriangle className="w-8 h-8 text-danger-500" />
        <p className="text-danger-600">Error al cargar los comentarios</p>
      </div>
    )
  }

  const chip = (key: string, label: string, count: number) => (
    <button
      key={key}
      onClick={() => setSupplier(key)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        supplier === key
          ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
      }`}
    >
      {label}
      <span className={`px-1.5 rounded-full text-[10px] ${supplier === key ? 'bg-white/20' : 'bg-white dark:bg-slate-800'}`}>{count}</span>
    </button>
  )
  const normal = all.filter((c) => !c.important)

  return (
    <div className="grid gap-6 lg:grid-cols-3 items-start">
      {/* Nuevo comentario */}
      <div className="lg:sticky lg:top-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30">
          <span className="p-1.5 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white"><Plus className="w-4 h-4" /></span>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white leading-tight">Nuevo comentario</h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Notas de la sección que no son de un pedido concreto</p>
          </div>
        </div>
        <div className="p-5">
          <CommentEditor
            key={composerKey}
            suppliers={data?.suppliers || []}
            submitLabel="Publicar"
            pending={create.isPending}
            error={create.isError ? apiError(create.error) : undefined}
            onSubmit={(v) => create.mutate(v, { onSuccess: () => setComposerKey((k) => k + 1) })}
          />
        </div>
      </div>

      {/* Listado */}
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar en los comentarios, autor o proveedor…"
              className="w-full pl-10 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {chip('', 'Todos', normal.length)}
            {chip('_none', 'Generales', normal.filter((c) => !c.supplier_code).length)}
            {usedSuppliers.map(([code, name]) => chip(code, name, normal.filter((c) => c.supplier_code === code).length))}
          </div>
        </div>

        {list.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-center">
            <span className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800"><MessagesSquare className="w-8 h-8 text-slate-400" /></span>
            <p className="font-medium text-slate-600 dark:text-slate-300">
              {normal.length ? 'Ningún comentario con estos filtros' : 'Todavía no hay comentarios generales'}
            </p>
            {!normal.length && (
              <p className="text-sm text-slate-500 max-w-sm">
                Escribe el primero a la izquierda: plazos que avisa un proveedor, acuerdos con el almacén, recordatorios para el equipo…
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((c) => (
              <GeneralCommentCard
                key={c.id}
                c={c}
                suppliers={data?.suppliers || []}
                canEdit={c.author === data?.current_user || !!data?.is_admin}
              />
            ))}
          </div>
        )}
        {all.some((c) => c.important) && (
          <p className="text-xs text-center text-slate-400">
            <Star className="inline w-3 h-3 -mt-0.5 text-amber-500 fill-current" /> Los comentarios importantes se muestran arriba, en «Avisos importantes».
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================================ Página

type Tab = 'pedidos' | 'comentarios'

function OrdersTab({ query, supplier, setSupplier }: {
  query: UseQueryResult<PendingPartsResponse>
  supplier: string
  setSupplier: (s: string) => void
}) {
  const [origin, setOrigin] = useState<OriginFilter>('all')
  const [search, setSearch] = useState('')
  const data = query.data
  const items = useMemo(() => {
    const q = search.trim().toUpperCase()
    return (data?.items || []).filter((p) => {
      if (origin !== 'all' && p.status !== origin) return false
      if (!q) return true
      return [p.reference, p.supplier_reference, p.description, p.customer, p.id_order ? String(p.id_order) : '', p.origin_label]
        .some((v) => (v || '').toUpperCase().includes(q))
    })
  }, [data, origin, search])

  const cards: { key: OriginFilter; label: string; value: number; icon: typeof Store; cls: string }[] = [
    { key: 'all', label: 'Todas', value: data?.totals.all ?? 0, icon: Hourglass, cls: 'from-slate-600 to-slate-800' },
    { key: 'rosa', label: 'PrestaShop · pagados', value: data?.totals.rosa ?? 0, icon: Store, cls: 'from-pink-500 to-rose-600' },
    { key: 'contrarreembolso', label: 'PrestaShop · contrarreembolso', value: data?.totals.contrarreembolso ?? 0, icon: Store, cls: 'from-amber-400 to-orange-500' },
    { key: 'manual', label: 'Añadidas a mano', value: data?.totals.manual ?? 0, icon: UserPlus, cls: 'from-indigo-500 to-violet-600' },
  ]

  return (
    <div className="space-y-6">
      {/* Tarjetas-filtro por origen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => setOrigin(c.key)}
            className={`text-left rounded-xl p-4 text-white bg-gradient-to-br ${c.cls} shadow-sm transition-all ${
              origin === c.key ? 'ring-4 ring-offset-2 ring-indigo-300 dark:ring-offset-slate-900 scale-[1.02]' : 'opacity-90 hover:opacity-100'
            }`}
          >
            <div className="flex items-center justify-between">
              <c.icon className="w-5 h-5 text-white/80" />
              <span className="text-3xl font-bold">{query.isLoading ? '…' : c.value}</span>
            </div>
            <p className="text-sm mt-2 text-white/90">{c.label}</p>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
        <select
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
          className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        >
          <option value="">Todos los proveedores</option>
          {(data?.suppliers || []).map((s) => (
            <option key={s.code} value={s.code}>{s.name || s.code}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar referencia, ref. proveedor, pedido, cliente o usuario…"
            className="w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-pink-500" /> Pagado</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Contrarreembolso (≤40 días)</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-b from-indigo-500 to-violet-500" /> Manual</span>
        </div>
      </div>

      {/* Listado */}
      {query.isLoading ? (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          <p className="text-slate-500">Cargando piezas pendientes…</p>
        </div>
      ) : query.isError ? (
        <div className="flex flex-col items-center gap-3 py-16">
          <AlertTriangle className="w-8 h-8 text-danger-500" />
          <p className="text-danger-600">Error al cargar las piezas pendientes</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-slate-500">
          <PackageCheck className="w-10 h-10 text-emerald-500" />
          <p>No hay piezas pendientes con estos filtros</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <PartCard key={p.key} part={p} currentUser={data!.current_user} isAdmin={data!.is_admin} />
          ))}
        </div>
      )}

    </div>
  )
}

export default function PendingPartsPage() {
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'comentarios' ? 'comentarios' : 'pedidos'
  const setTab = (t: Tab) => setParams(t === 'pedidos' ? {} : { tab: t }, { replace: true })
  const [supplier, setSupplier] = useState('')
  const [showAdd, setShowAdd] = useState(false)

  const query = useQuery({
    queryKey: ['pending-parts', supplier],
    queryFn: async () => {
      const res = await api.get<PendingPartsResponse>('/reports/pending-parts', { params: supplier ? { supplier } : {} })
      return res.data
    },
  })
  const general = useGeneralComments()
  const comments = general.data?.comments || []
  const importantCount = comments.filter((c) => c.important).length
  const active = tab === 'pedidos' ? query : general

  const tabs: { key: Tab; label: string; icon: typeof Store; count: number | null }[] = [
    { key: 'pedidos', label: 'Pedidos', icon: ClipboardList, count: query.data?.totals.all ?? null },
    { key: 'comentarios', label: 'Comentarios generales', icon: MessagesSquare, count: general.data ? comments.length : null },
  ]

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="p-2 bg-pink-100 dark:bg-pink-900/40 rounded-lg">
            <Hourglass className="w-8 h-8 text-pink-600 dark:text-pink-400" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Piezas pendientes</h1>
            <p className="text-slate-500 dark:text-slate-400">
              Pedidos de la web sin stock físico y notas del equipo para gestionarlos.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => active.refetch()}
            disabled={active.isFetching}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 ${active.isFetching ? 'animate-spin' : ''}`} /> Actualizar
          </button>
          {tab === 'pedidos' && (
            <button
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium shadow-sm hover:shadow-md transition-shadow"
            >
              <Plus className="w-4 h-4" /> Añadir pieza pendiente
            </button>
          )}
        </div>
      </div>

      {/* Avisos importantes (en las dos pestañas) */}
      {general.data && <ImportantBoard data={general.data} />}

      {/* Pestañas */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="-mb-px flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => {
            const on = tab === t.key
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={on}
                onClick={() => setTab(t.key)}
                className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  on
                    ? 'border-indigo-600 text-indigo-700 dark:border-indigo-400 dark:text-indigo-300'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
                {t.count !== null && (
                  <span className={`min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-[11px] font-bold text-center ${
                    on ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                  }`}>
                    {t.count}
                  </span>
                )}
                {t.key === 'comentarios' && importantCount > 0 && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-500 text-white" title="Comentarios importantes">
                    <Star className="w-3 h-3 fill-current" /> {importantCount}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {tab === 'pedidos'
        ? <OrdersTab query={query} supplier={supplier} setSupplier={setSupplier} />
        : <GeneralCommentsTab query={general} />}

      {showAdd && <AddPartModal onClose={() => setShowAdd(false)} catalogSuppliers={query.data?.catalog_suppliers || []} />}
    </div>
  )
}
